import {DeleteOutline, EditOutlined, GroupsOutlined, KeyOutlined, MoreVert, PeopleOutline} from '@mui/icons-material';
import {Alert, Box, Grid, IconButton, Menu, MenuItem, Stack, Typography} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useNavigate, useParams} from 'react-router-dom';
import {
    useDeletePermissionMutation,
    useGetPermissionCatalogQuery,
    useGetRolesQuery,
} from 'src/api';
import AMPModal from 'src/components/common/AMPModal';
import {ConformationModal} from 'src/components/common/ConformationModal';
import {PageHeader} from 'src/components/common/PageHeader';
import {PermissionFormModal} from 'src/components/iam/PermissionFormModal';
import {RoleRow} from 'src/components/iam/RoleRow';
import {IAM_TONE, calloutSx, listRowSx, pillSx, subjectBadgeSx} from 'src/components/iam/iam.sx';
import {KpiCard} from 'src/pages/EnergyConsumptionReport/components/KpiCard';
import {useCan} from 'src/permissions/hooks';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import theme from 'src/theme';
import {cardSx, emptySx} from '../AccessControl/AccessControl.sx';
import {useBusinessId} from 'src/app/hooks';

/**
 * One permission: what it means, and who ends up with it.
 *
 * The number that decides whether to grant something is not how many roles carry it but how many
 * people those roles reach, so both are on the page. The delete control is offered unconditionally
 * and the server is the authority — a permission a live endpoint still checks cannot go, and the
 * refusal is turned into an explanation here rather than an unexplained red toast.
 */
export const PermissionInfo: FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const params = useParams();
    const permissionName = params.permissionName ?? '';

    const canManageCatalog = useCan('accessControl.manageCatalog');
    const {data: catalog} = useGetPermissionCatalogQuery();
    // Roles belong to a business, so the list follows the one being worked in.
    const businessId = useBusinessId();
    const {data: roles} = useGetRolesQuery(businessId);
    const [deletePermission] = useDeletePermissionMutation();

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [editing, setEditing] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleteRefusal, setDeleteRefusal] = useState<string | null>(null);

    const permission = useMemo(
        () => (catalog ?? []).find((candidate) => candidate.name === permissionName),
        [catalog, permissionName]
    );

    const granting = useMemo(
        () =>
            (roles ?? []).filter((role) =>
                role.permissions?.some((candidate) => candidate.name === permissionName)
            ),
        [roles, permissionName]
    );

    const peopleAffected = useMemo(
        () => granting.reduce((total, role) => total + (role.holders ?? 0), 0),
        [granting]
    );

    const handleDelete = async () => {
        try {
            await deletePermission(permissionName).unwrap();
            NotifySuccess(intl.formatMessage({id: 'deleted', defaultMessage: 'Deleted'}));
            navigate(
                AppRouteSpace([AppRouteEnum.app, AppRouteEnum.accessControl]) + '?section=permissions'
            );
        } catch (error) {
            const message = isErrorObject(error)
                ? error.data.message
                : intl.formatMessage({id: 'somethingWentWrong', defaultMessage: 'Something went wrong'});
            // The one operation here that can legitimately fail. Saying why, on the page, is more use
            // than a toast that vanishes before it has been read.
            setDeleteRefusal(message);
            NotifyError(message);
        } finally {
            setConfirmingDelete(false);
        }
    };

    if (!permission) {
        return (
            <Alert severity="warning" sx={{mt: 4, borderRadius: '14px'}}>
                <FormattedMessage
                    id="accessControlPermissionMissing"
                    defaultMessage="No such permission in the catalog."
                />
            </Alert>
        );
    }

    return (
        <Box sx={{pb: 6}}>
            <PageHeader
                title={intl.formatMessage({
                    id: `permission.${permission.subject}.${permission.action}`,
                    defaultMessage: permission.name,
                })}
                icon={<KeyOutlined/>}
                onlyTitle
                titleSuffix={
                    <Stack direction="row" spacing={1}>
                        {permission.enforced === false && (
                            <Typography sx={pillSx(IAM_TONE.none)}>
                                {intl.formatMessage({
                                    id: 'accessControlNotEnforced',
                                    defaultMessage: 'Not enforced yet',
                                })}
                            </Typography>
                        )}
                    </Stack>
                }
            >
                {canManageCatalog && (
                    <>
                        <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
                            <MoreVert/>
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchor}
                            open={!!menuAnchor}
                            onClose={() => setMenuAnchor(null)}
                        >
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchor(null);
                                    setEditing(true);
                                }}
                            >
                                <EditOutlined fontSize="small" sx={{mr: 1}}/>
                                <FormattedMessage id="edit" defaultMessage="Edit"/>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setMenuAnchor(null);
                                    setConfirmingDelete(true);
                                }}
                            >
                                <DeleteOutline fontSize="small" sx={{mr: 1}}/>
                                <FormattedMessage id="delete" defaultMessage="Delete"/>
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </PageHeader>

            <Typography sx={{fontSize: '0.8125rem', color: theme.palette.neutral[400]}}>
                {permission.name}
            </Typography>
            {permission.description && (
                <Typography
                    sx={{mt: 1, fontSize: '0.875rem', color: theme.palette.grey[500], maxWidth: 760}}
                >
                    {permission.description}
                </Typography>
            )}


            {permission.enforced === false && (
                <Alert severity="info" sx={{mt: 2, borderRadius: '14px'}}>
                    <FormattedMessage
                        id="accessControlNotEnforcedExplained"
                        defaultMessage="Not enforced yet. No backend code checks for this permission, so granting it currently changes nothing. It starts working the moment a handler declares it."
                    />
                </Alert>
            )}

            {deleteRefusal && (
                <Alert severity="warning" sx={{mt: 2, borderRadius: '14px'}}>
                    <Typography sx={{fontWeight: 600, mb: 0.5}}>
                        <FormattedMessage
                            id="accessControlPermissionStillInUse"
                            defaultMessage="Still in use."
                        />
                    </Typography>
                    <Typography sx={{fontSize: '0.875rem'}}>{deleteRefusal}</Typography>
                </Alert>
            )}

            <Grid container spacing={2} sx={{mt: 0.5}}>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlGrantedBy',
                            defaultMessage: 'Granted by',
                        })}
                        value={String(granting.length)}
                        unit={intl.formatMessage({
                            id: 'accessControlRolesUnit',
                            defaultMessage: 'roles',
                        })}
                        icon={<KeyOutlined fontSize="small"/>}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlPeopleAffected',
                            defaultMessage: 'People affected',
                        })}
                        value={String(peopleAffected)}
                        unit={intl.formatMessage({
                            id: 'accessControlHoldSuchRole',
                            defaultMessage: 'hold a role that includes it',
                        })}
                        icon={<PeopleOutline fontSize="small"/>}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2.4}>
                    <KpiCard
                        label={intl.formatMessage({
                            id: 'accessControlArea',
                            defaultMessage: 'Area',
                        })}
                        value={intl.formatMessage({
                            id: `permissionSubject.${permission.subject}`,
                            defaultMessage: permission.subject,
                        })}
                        icon={<GroupsOutlined fontSize="small"/>}
                    />
                </Grid>
            </Grid>

            <Box sx={calloutSx(granting.length ? IAM_TONE.granted : IAM_TONE.refused)}>
                <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                    <FormattedMessage id="accessControlGrantedBy" defaultMessage="Granted by"/>
                </Typography>
            </Box>

            <Box sx={cardSx}>
                {granting.length === 0 ? (
                    <Box sx={emptySx}>
                        <Typography sx={{fontWeight: 600, mb: 0.5}}>
                            <FormattedMessage
                                id="accessControlGrantedByNobody"
                                defaultMessage="No role grants this, so nobody can do it."
                            />
                        </Typography>
                        <Typography sx={{fontSize: '0.875rem'}}>
                            <FormattedMessage
                                id="accessControlGrantedByNobodyHint"
                                defaultMessage="If someone needs it, add it to the role they already hold rather than creating a role for one permission."
                            />
                        </Typography>
                    </Box>
                ) : (
                    granting.map((role) => <RoleRow key={role.id} role={role}/>)
                )}
            </Box>

            {editing && (
                <PermissionFormModal permission={permission} onClose={() => setEditing(false)}/>
            )}

            <AMPModal
                open={confirmingDelete}
                handleClose={() => setConfirmingDelete(false)}
                title={intl.formatMessage({
                    id: 'accessControlDeletePermission',
                    defaultMessage: 'Delete permission',
                })}
                type="sm"
                fullWidth
            >
                <ConformationModal
                    text="accessControlDeletePermissionConfirm"
                    caption={permission.name}
                    handleClose={() => setConfirmingDelete(false)}
                    handleSubmit={handleDelete}
                />
            </AMPModal>
        </Box>
    );
};
